import classNames from 'classnames';
import EcoIconSwiperArrow from 'eco-design-micro/icons/eco-icon-swiper-arrow';
import EcoButtonDouble from 'eco-design/base/eco-button-double';
import EcoImg from 'eco-design/base/eco-img';
import React, { useEffect, useRef, useState } from 'react';
import { Navigation } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/scss';
import './index.scss';
import { EcoCompareProductItemProps, EcoCompareProductProps, EcoCompareProductSpecItemsProps, EcoCompareProductSubItemProps } from './props';

/**
 * 产品对比,在css写法上主要有2种;即以【行】还是以【列】为第1遍历方式
 *
 * 第1种,苹果官网的写法,以【列】为第1遍历,给每个参数子项添加最小高度;以实现对齐;(代价是需要需要计算每行中的最小高度并赋值)
 *
 * 第2种,大疆和安克的写法,以【行】为第1遍历,使用flex或table布局实现对齐;(代价是需要将数据分成一行一行的)
 *
 * 此处选择以【列】为第1遍历方式,即以苹果官网的写法为主,给每个子项设置最小高度
 */
const EcoCompareProduct: React.FC<EcoCompareProductProps> = ({ theme = 'white', items = [], className, isOpenLazy = false }) => {
    const prefixCls = `eco-compare-product`;
    const length = (items || []).length;
    const [itemsB, setItemsB] = useState(items);
    const refA = useRef(null);
    const compCls = classNames(prefixCls, {
        [`${className}`]: className,
        [`${prefixCls}-theme-${theme}`]: theme,
        [`${prefixCls}-length-${length}`]: length,
    });
    // 给每个子项设置最小高度以对齐
    const setItemMinHeight = () => {
        const listDomsA = (refA.current as any).querySelectorAll('.eco-compare-product-sub-item-spec-box');
        // 收集每列的最小高度
        const minHeightAObj: any = {};
        for (let i = 0; i < listDomsA.length; i++) {
            const elemA = listDomsA[i];
            const listDomsB = elemA.querySelectorAll('.eco-compare-product-sub-item-spec-item');
            for (let j = 0; j < listDomsB.length; j++) {
                const elemB = listDomsB[j];
                const clientHeight = elemB.clientHeight;
                minHeightAObj[j] = Math.max(minHeightAObj[j] || 0, clientHeight);
            }
        }
        // 设置最小高度
        for (let i = 0; i < listDomsA.length; i++) {
            const elemA = listDomsA[i];
            const listDomsB = elemA.querySelectorAll('.eco-compare-product-sub-item-spec-item');
            for (let j = 0; j < listDomsB.length; j++) {
                const elemB = listDomsB[j];
                elemB.style.minHeight = `${minHeightAObj[j]}px`;
            }
        }
        // 头部
        const listDomsB = (refA.current as any).querySelectorAll('.eco-compare-product-sub-item-h');
        let minHeightB = 100;
        for (let i = 0; i < listDomsB.length; i++) {
            const elemA = listDomsB[i];
            const clientHeight = elemA.clientHeight;
            minHeightB = Math.max(minHeightB, clientHeight);
        }
        // 设置最小高度
        for (let i = 0; i < listDomsB.length; i++) {
            const elemA = listDomsB[i];
            elemA.style.minHeight = `${minHeightB}px`;
        }
        const listDomsC = (refA.current as any).querySelectorAll('.eco-compare-product-sub-item-title');
        let minHeightC = 28;
        for (let i = 0; i < listDomsC.length; i++) {
            const elemA = listDomsC[i];
            const clientHeight = elemA.clientHeight;
            minHeightC = Math.max(minHeightC, clientHeight);
        }
        // 设置最小高度
        for (let i = 0; i < listDomsC.length; i++) {
            const elemA = listDomsC[i];
            elemA.style.minHeight = `${minHeightC}px`;
        }
    };
    useEffect(() => {
        setItemMinHeight();
    }, []);
    const [spaceBetweenN, setSpaceBetweenN] = useState(32);
    useEffect(() => {
        const initHandle = () => {
            const clientWidth = document.body.clientWidth;
            if (clientWidth >= 1024) {
                setSpaceBetweenN(32);
            } else {
                setSpaceBetweenN(16);
            }
        };
        initHandle();
    }, []);
    return (
        <div className={compCls} ref={refA}>
            <Swiper
                slidesPerView={'auto'}
                spaceBetween={spaceBetweenN}
                navigation={{
                    nextEl: '.eco-swiper-button-next',
                    prevEl: '.eco-swiper-button-prev',
                }}
                modules={[Navigation]}
            >
                {itemsB?.map((item: EcoCompareProductItemProps, index1: number) => {
                    const subItems = item.subItems || []; //每个子项里面可能是多个的,所以它应当是个二维数组
                    return (
                        <SwiperSlide key={index1} className={`${prefixCls}-item __item-${index1 + 1} ${prefixCls}-item-sub-len${subItems.length}`}>
                            {subItems.map((subItem1: EcoCompareProductSubItemProps, index2: number) => {
                                const prefixSubCls = `${prefixCls}-sub-item`;
                                // const activeIndex = subItem1.activeIndex || 0;
                                const { activeIndex = 0, specItems = [], ...rest } = subItem1;
                                const isActive = activeIndex === index2;
                                const subCls = classNames(prefixSubCls, {
                                    [`${prefixSubCls}-active`]: activeIndex === index2,
                                });
                                // const specItems = subItem1.specItems || [];
                                // 只展示选中的一个
                                if (!isActive) {
                                    return null;
                                }
                                return (
                                    <div key={index2} className={subCls}>
                                        <div className={`${prefixSubCls}-h`}>
                                            <div className={`${prefixSubCls}-img`}>
                                                <EcoImg pc={subItem1.pc} isOpenLazy={isOpenLazy} />
                                            </div>
                                            <div className={`${prefixSubCls}-tool`}>
                                                {subItems.map((subItem2: EcoCompareProductSubItemProps, index3: number) => {
                                                    const compStyle = {
                                                        '--eco-compare-product-sub-item-tool-color': subItem2.color || 'black',
                                                    } as React.CSSProperties;
                                                    return <div className={`${prefixSubCls}-tool-item`} key={index3} style={compStyle}></div>;
                                                })}
                                            </div>
                                            <div className={`${prefixSubCls}-section`}>
                                                <div className={`${prefixSubCls}-label`}>{subItem1.label}</div>
                                                {subItem1?.title && <div className={`${prefixSubCls}-title`}>{subItem1.title}</div>}
                                                {subItem1?.desc && <div className={`${prefixSubCls}-desc`}>{subItem1.desc}</div>}
                                            </div>

                                            <div className={`${prefixSubCls}-oper`}>
                                                {subItem1?.price && <div className={`${prefixSubCls}-price`}>{subItem1.price}</div>}
                                                <EcoButtonDouble
                                                    lChildren={subItem1.lChildren}
                                                    rChildren={subItem1.rChildren}
                                                    lRadius="ellipse"
                                                    rType="text"
                                                    rIsWithRightArrow={true}
                                                    lHref={subItem1.lHref}
                                                    rHref={subItem1.rHref}
                                                    className={`${prefixSubCls}-btns`}
                                                    lClassName="GtmModuleData-btn-href l-item-btn"
                                                    rClassName="GtmModuleData-btn-href r-item-btn"
                                                    lDataLayer={{
                                                        __btn_id1: `${prefixSubCls} .l-item-btn`,
                                                        __btn_id2: subItem1.lHref,
                                                        __btn_type: 'href',
                                                    }}
                                                    rDataLayer={{
                                                        __btn_id1: `${prefixSubCls} .r-item-btn`,
                                                        __btn_id2: subItem1.rHref,
                                                        __btn_type: 'href',
                                                    }}
                                                    {...rest}
                                                    lBtnClick={(e) => {
                                                        subItem1?.lBtnClick?.(e, index1);
                                                    }}
                                                />
                                            </div>
                                            <div className={`${prefixSubCls}-border`}></div>
                                        </div>
                                        <div className={`${prefixSubCls}-spec-box`}>
                                            {specItems.map((specItem: EcoCompareProductSpecItemsProps, specIndex: number) => {
                                                return (
                                                    <div key={specIndex} className={`${prefixSubCls}-spec-item`}>
                                                        <div className={`${prefixSubCls}-spec-title`}>{specItem.title}</div>
                                                        <div className={`${prefixSubCls}-spec-desc`}>{specItem.desc}</div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                );
                            })}
                        </SwiperSlide>
                    );
                })}
                <div className={`eco-swiper-control eco-swiper-control-${theme}`}>
                    <div className="eco-swiper-navigation">
                        <div className="eco-swiper-button-prev">
                            <EcoIconSwiperArrow className="eco-swiper-button-icon" />
                        </div>
                        <div className="eco-swiper-button-next">
                            <EcoIconSwiperArrow className="eco-swiper-button-icon" />
                        </div>
                    </div>
                </div>
            </Swiper>
        </div>
    );
};

export default EcoCompareProduct;
